const loaderInstance = require('../components/loader');
const helper = require('./helper');
const $loaderContainer = document.querySelector('.paypalLoader');
const loader = loaderInstance($loaderContainer);
const CONTENT_TYPE_JSON = 'application/json';

/**
 * Create billing formData from fields data
 *
 * @param {Object} fieldsData - fields data values
 * @param {Element} $button - button element
 * @returns {Object} cart billing form data
 */
function createCartBillingFormData(fieldsData, $button) {
    const cartBillingFormData = new FormData();

    const cartBillingFields = $button && JSON.parse($button.getAttribute('data-paypal-billing-form-fields'));

    Object.entries(cartBillingFields)
        .forEach(entry => {
            const [key, field] = entry;

            if (typeof field === 'object') {
                cartBillingFormData.append(field.name, fieldsData && fieldsData[key] ? fieldsData[key] : field.value);
            }
        });

    return cartBillingFormData;
}

/**
 * Calls to Paypal-ValidatePayPalShippingAddress endpoint
 * @param {Object} shippingAddress Shipping address from PayPal
 */
async function validatePayPalShippingAddress(shippingAddress) {
    const response = await fetch(helper.getUrlWithCsrfToken(window.paypalUrls.validatePayPalShippingAddress), {
        method: 'POST',
        headers: {
            'Content-Type': CONTENT_TYPE_JSON
        },
        body: JSON.stringify({
            shippingAddress: shippingAddress
        })
    });

    return response.json();
}

/**
 * Calls to Paypal-UpdateBasketShipment endpoint
 * @param {Object} shippingAddress Shipping address from PayPal
 */
async function updateShipment(shippingAddress) {
    try {
        const response = await fetch(helper.getUrlWithCsrfToken(window.paypalUrls.updateShipment), {
            method: 'POST',
            headers: {
                'Content-Type': CONTENT_TYPE_JSON
            },
            body: JSON.stringify({
                shippingAddress: shippingAddress
            })
        });

        return response.json();
    } catch (e) {
        return { success: false };
    }
}

/**
 * Call FinishLPM endpoint
 * @param {string} fundingSource The current paypal funding source
 * @returns {Promise} ajax call
 */
function finishLpmFlow(fundingSource) {
    const $usedPaymentMethod = document.getElementById('usedPaymentMethod');
    const pmName = $usedPaymentMethod ? $usedPaymentMethod.value : fundingSource;

    return $.ajax({
        url: helper.getUrlWithCsrfToken(window.paypalUrls.finishLpm),
        type: 'POST',
        contentType: CONTENT_TYPE_JSON,
        data: JSON.stringify({
            pmName
        })
    });
}

/**
 * Makes call on saving a PayPal default address to the storefront customer.
 * @param {Object} addressObject PayPal address information.
 * @param {boolean} isAccountPage Identifies whether the source page is account.
 * @returns {Object} Whether a success or failed JSON response body.
 */
function savePaypalDefaultAddress(addressObject, isAccountPage) {
    return $.ajax({
        url: window.paypalUrls.savePaypalDefaultAddress,
        type: 'POST',
        contentType: CONTENT_TYPE_JSON,
        data: JSON.stringify({ addressObject, isAccountPage })
    });
}

/**
 * Finish a LPM payment
 * @param {string} usedPaymentMethod Current payment method name
 */
function finishLpmPayment(usedPaymentMethod) {
    finishLpmFlow(usedPaymentMethod)
        .then(({ redirectUrl }) => {
            loader.hide();
            helper.processConfirmForm(redirectUrl);
        })
        .catch(function() {
            loader.hide();
        });
}

/**
 * Creates and sets a payPal order id
 * @param {Object} paymentSourceData The payment source data (card, apple_pay...))
 * @param {boolean} isExpressCheckout - indicates if it is EC flow
 * @returns {string} paypal order id
 */
function getPaypalOrderId(paymentSourceData, isExpressCheckout = false) {
    let url = helper.getUrlWithCsrfToken(window.paypalUrls.getPaypalOrderId);

    url = [url, '&isExpressCheckout=', isExpressCheckout].join('');
    url = paymentSourceData ? [url, '&paymentSourceData=', JSON.stringify(paymentSourceData)].join('') : url;

    return $.ajax({
        url: url,
        type: 'GET',
        contentType: CONTENT_TYPE_JSON,
        async: false
    }).responseJSON;
}

/**
 * Create the Apple pay order
 * @param {Object} paymentSource The Apple pay payment source object
 * @returns {Promise} The promise resolved with the data object
 */
const createApplePayOrder = paymentSource => {
    return fetch(helper.getUrlWithCsrfToken(window.paypalUrls.createApplePayOrder), {
        method: 'POST',
        headers: {
            'Content-Type': CONTENT_TYPE_JSON
        },
        body: JSON.stringify(paymentSource)
    })
        .then(response => response.json());
};

/**
 * Gets the basket data
 * @returns {Promise} The promise resolved with the basket data
 */
const getBasketData = () => {
    const url = helper.getUrlWithCsrfToken(window.paypalUrls.getBasketData);

    return $.ajax({
        url: url,
        type: 'GET',
        contentType: CONTENT_TYPE_JSON,
        async: false
    }).responseJSON;
};

/**
 * Gets the order billing address data
 * @returns {Promise} The promise resolved with the order billing address data
 */
const getOrderBillingAddress = () => {
    const url = helper.getUrlWithCsrfToken(window.paypalUrls.getOrderBillingAddress);

    return $.ajax({
        url: url,
        type: 'GET',
        contentType: CONTENT_TYPE_JSON,
        async: false
    }).responseJSON;
};

/**
 * Creates Payment Token and adds Paypal account
 * @param {Object} params - Additional parameters
 * @param {boolean} [params.isAPMA] - Additional payment method adding flow
 */
async function addPaypalAccount(params = {}) {
    let url = helper.getUrlWithCsrfToken(window.paypalUrls.accountAddPaypalHandler);

    if (params.isAPMA) {
        url += '&isAPMA=true';
    }

    const response = await fetch(url, {
        method: 'POST'
    });

    return response.json();
}

/**
 * Creates Setup Token
 */
async function createSetupToken() {
    const response = await fetch(helper.getUrlWithCsrfToken(window.paypalUrls.createSetupTokenForPaypal), {
        method: 'POST'
    });

    return response.json();
}

export {
    addPaypalAccount,
    createSetupToken,
    finishLpmFlow,
    createCartBillingFormData,
    savePaypalDefaultAddress,
    finishLpmPayment,
    getPaypalOrderId,
    validatePayPalShippingAddress,
    updateShipment,
    createApplePayOrder,
    getBasketData,
    getOrderBillingAddress
};
